import { useEffect, useState } from 'react';
import { Box, Typography, Card, CardContent, Grid, Stack, TextField, Autocomplete, Stepper, Step, StepLabel } from '@mui/material';
import { checkUsernameAvailable } from '../../lib/api';
import { track } from '../../lib/analytics';
import { navigate } from '../../lib/navigation';
import { COUNTRIES } from '../../lib/countries';
import { CREATOR_TYPE_OPTIONS, NICHE_OPTIONS } from '../../lib/creatorOptions';
import { countryFlag } from '../../lib/social';
import Button from '../../components/Button';
import LogoMark from '../../components/LogoMark';
import EditableTagEditor from '../../components/EditableTagEditor';
import Panel from '../../components/Panel';
import ProfilePreview from '../../components/ProfilePreview';
import { normalizeUsername, usernameError } from '../../lib/profileReadiness';

function OnboardingPage({ identity, identityActions }) {
  const steps = ['Profile basics', 'Creator niche', 'All set'];
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ ...identity.creator });
  const [usernameCheck, setUsernameCheck] = useState({ status: 'idle', message: '' });
  const [finishing, setFinishing] = useState(false);
  const canAdvance = step !== 0 || (form.displayName && form.username && form.headline && usernameCheck.status === 'available');

  useEffect(() => {
    track('onboarding_step_viewed', { label: steps[step], entityId: String(step) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  async function checkUsername() {
    const validationMessage = usernameError(form.username);
    if (validationMessage) {
      setUsernameCheck({ status: 'error', message: validationMessage });
      return;
    }
    setUsernameCheck({ status: 'checking', message: '' });
    try {
      const available = await checkUsernameAvailable(normalizeUsername(form.username));
      track('username_availability_checked', { label: available ? 'available' : 'taken' });
      setUsernameCheck(
        available ? { status: 'available', message: 'Username is available' } : { status: 'taken', message: 'Username is already taken' }
      );
    } catch {
      setUsernameCheck({ status: 'error', message: 'Could not check availability, try again' });
    }
  }

  async function finish() {
    setFinishing(true);
    try {
      await identityActions.completeOnboarding({ ...form, username: normalizeUsername(form.username), published: false });
      navigate('/dashboard');
    } catch {
      setFinishing(false);
    }
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center', p: 3 }}>
      <Card sx={{ maxWidth: step === 0 ? 960 : 720, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ mb: 3 }}>
            <LogoMark />
          </Box>
          <Stepper activeStep={step} sx={{ mb: 4 }} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          {step === 0 && (
            <Grid container spacing={4}>
              <Grid size={{ xs: 12, md: 7 }}>
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <Typography color="text.secondary">Let's start with the basics — this becomes your CreatorCV profile.</Typography>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Display name"
                      value={form.displayName}
                      onChange={(event) => setForm({ ...form, displayName: event.target.value })}
                    />
                  </Grid>
                  <Grid size={12}>
                    <Stack spacing={1}>
                      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                        <TextField
                          fullWidth
                          label="Username"
                          helperText={`This will be your CreatorCV url (creatorcv.app/profile/${form.username || 'xxx'})`}
                          value={form.username}
                          onChange={(event) => {
                            setForm({ ...form, username: event.target.value.replace(/\s/g, '').toLowerCase() });
                            setUsernameCheck({ status: 'idle', message: '' });
                          }}
                        />
                        <Button
                          variant="secondary"
                          disabled={!form.username.trim() || usernameCheck.status === 'checking'}
                          onClick={checkUsername}
                          sx={{ flexShrink: 0, mt: 1 }}
                        >
                          {usernameCheck.status === 'checking' ? 'Checking…' : 'Check availability'}
                        </Button>
                      </Stack>
                      {usernameCheck.message && (
                        <Typography variant="caption" sx={{ color: usernameCheck.status === 'available' ? 'success.main' : 'error.main' }}>
                          {usernameCheck.message}
                        </Typography>
                      )}
                    </Stack>
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      label="Professional headline"
                      placeholder="Write your professional headline"
                      helperText={(
                        <>
                          Describe what you create and who it's for in one sentence.
                          <Box component="span" sx={{ display: 'block', mt: 0.25, fontSize: '0.72rem', fontStyle: 'italic', color: 'text.disabled' }}>
                            Example: Beauty creator specialising in skincare reviews and tutorials.
                          </Box>
                        </>
                      )}
                      value={form.headline}
                      onChange={(event) => setForm({ ...form, headline: event.target.value })}
                    />
                  </Grid>
                  <Grid size={12}>
                    <Autocomplete
                      fullWidth
                      options={COUNTRIES}
                      value={form.location || null}
                      onChange={(event, newValue) => setForm({ ...form, location: newValue || '' })}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} sx={{ gap: 1 }}>
                          <span>{countryFlag(option)}</span> {option}
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Location"
                          slotProps={{
                            ...params.slotProps,
                            input: {
                              ...params.slotProps?.input,
                              startAdornment: form.location ? <Box sx={{ pl: 0.5 }}>{countryFlag(form.location)}</Box> : null,
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid size={{ xs: 12, md: 5 }}>
                <Typography variant="overline" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Preview
                </Typography>
                <ProfilePreview creator={{ ...identity.creator, ...form }} />
              </Grid>
            </Grid>
          )}
          {step === 1 && (
            <Stack spacing={3}>
              <EditableTagEditor
                label="Creator types"
                placeholder="Add custom type"
                values={form.creatorTypes}
                onChange={(creatorTypes) => setForm({ ...form, creatorTypes })}
                options={CREATOR_TYPE_OPTIONS}
              />
              <EditableTagEditor
                label="Niches"
                placeholder="Add custom niche"
                values={form.niches}
                onChange={(niches) => setForm({ ...form, niches })}
                options={NICHE_OPTIONS}
              />
            </Stack>
          )}
          {step === 2 && (
            <Stack spacing={2}>
              <Panel title="Your draft is ready" text="Complete your profile photo in the dashboard, then publish when you're ready." />
              <Stack spacing={1.5}>
                <Typography>• Add your social accounts and stats</Typography>
                <Typography>• Showcase campaigns and content you've worked on</Typography>
                <Typography>• Tag the collaborators you've worked with</Typography>
              </Stack>
            </Stack>
          )}
          <Stack direction="row" spacing={1} sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: 'divider', justifyContent: 'flex-end' }}>
            <Button variant="ghost" disabled={step === 0 || finishing} onClick={() => setStep(step - 1)}>
              Back
            </Button>
            {step < steps.length - 1 && (
              <Button variant="ghost" onClick={() => setStep(step + 1)}>
                Skip
              </Button>
            )}
            {step < steps.length - 1 ? (
              <Button disabled={!canAdvance} onClick={() => setStep(step + 1)}>
                Next
              </Button>
            ) : (
              <Button disabled={finishing} onClick={finish}>{finishing ? 'Saving…' : 'Finish and enter dashboard'}</Button>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}

export default OnboardingPage;
